import { A, useNavigate, useParams } from '@solidjs/router';
import { type Component, createEffect, createResource, createSignal, Show } from 'solid-js';
import { createProject, fetchProject, type Project, updateProject } from '../services/api';

const ProjectBuilder: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditMode = () => !!params.id;

  // Form state
  const [name, setName] = createSignal('');
  const [goal, setGoal] = createSignal('');
  const [status, setStatus] = createSignal<Project['status']>('draft');
  const [visibility, setVisibility] = createSignal<Project['visibility']>('private');
  const [runMode, setRunMode] = createSignal<Project['run_mode']>('both');

  // Vision fields (collapsible)
  const [mission, setMission] = createSignal('');
  const [problem, setProblem] = createSignal('');
  const [showVision, setShowVision] = createSignal(false);

  // UI state
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = createSignal(false);

  // Load existing project if editing
  const [existingProject] = createResource(
    () => (isEditMode() ? params.id : null),
    async (id) => {
      if (!id) return null;
      return fetchProject(id);
    },
  );

  // Populate form when project loads
  createEffect(() => {
    const project = existingProject();
    if (project) {
      setName(project.name);
      setGoal(project.goal);
      setStatus(project.status);
      setVisibility(project.visibility);
      setRunMode(project.run_mode);
      if (project.vision) {
        setMission(project.vision.mission || '');
        setProblem(project.vision.problem || '');
        if (project.vision.mission || project.vision.problem) {
          setShowVision(true);
        }
      }
      setHasUnsavedChanges(false);
    }
  });

  // Track changes
  const markChanged = () => setHasUnsavedChanges(true);

  const handleSave = async () => {
    if (!name().trim()) {
      setError('Project name is required');
      return;
    }
    if (!goal().trim()) {
      setError('Project goal is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const projectData: Partial<Project> = {
        name: name().trim(),
        goal: goal().trim(),
        status: status(),
        visibility: visibility(),
        run_mode: runMode(),
        vision: {
          mission: mission().trim() || undefined,
          problem: problem().trim() || undefined,
        },
      };

      if (isEditMode()) {
        await updateProject(params.id, projectData);
        setHasUnsavedChanges(false);
        navigate(`/projects/${params.id}`);
      } else {
        const newProject = await createProject(projectData);
        setHasUnsavedChanges(false);
        // Navigate to the newly created project
        navigate(`/projects/${newProject.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const getCompletionScore = () => {
    let score = 0;
    if (name().trim()) score += 25;
    if (goal().trim()) score += 25;
    if (mission().trim()) score += 25;
    if (problem().trim()) score += 25;
    return score;
  };

  return (
    <div class="max-w-4xl mx-auto">
      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-4">
          <A
            href="/projects"
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </A>
          <div class="flex-1">
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              {isEditMode() ? 'Edit Project' : 'Create Project'}
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              {isEditMode() ? 'Update your project configuration' : 'Define a new AI agent project'}
            </p>
          </div>
          <Show when={hasUnsavedChanges()}>
            <span class="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs">
              Unsaved changes
            </span>
          </Show>
        </div>

        {/* Progress indicator */}
        <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-400">Completion</span>
            <span class="text-sm font-medium text-gray-300">{getCompletionScore()}%</span>
          </div>
          <div class="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300"
              style={{ width: `${getCompletionScore()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Error message */}
      <Show when={error()}>
        <div class="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
          {error()}
        </div>
      </Show>

      {/* Loading state for edit mode */}
      <Show when={isEditMode() && existingProject.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Form */}
      <Show when={!isEditMode() || !existingProject.loading}>
        <div class="space-y-6">
          {/* Basic Info Section */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
            <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-purple-400"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Basic Information
            </h2>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Project Name <span class="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={name()}
                  onInput={(e) => {
                    setName(e.currentTarget.value);
                    markChanged();
                  }}
                  placeholder="e.g., Customer Support Agent"
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Goal <span class="text-red-400">*</span>
                </label>
                <textarea
                  value={goal()}
                  onInput={(e) => {
                    setGoal(e.currentTarget.value);
                    markChanged();
                  }}
                  placeholder="What should this project accomplish? Describe in natural language..."
                  rows={3}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Describe the main objective. AI will use this to guide execution.
                </p>
              </div>
            </div>
          </section>

          {/* Vision Section (Collapsible) */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowVision(!showVision())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-blue-400"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Vision & Context</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showVision() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showVision()}>
              <div class="px-6 pb-6 space-y-4 border-t border-gray-700 pt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Mission Statement
                  </label>
                  <textarea
                    value={mission()}
                    onInput={(e) => {
                      setMission(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="What is the broader mission of this project?"
                    rows={2}
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Problem Being Solved
                  </label>
                  <textarea
                    value={problem()}
                    onInput={(e) => {
                      setProblem(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="What problem does this project solve for users?"
                    rows={2}
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  />
                </div>
              </div>
            </Show>
          </section>

          {/* Settings Section */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
            <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-gray-400"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Settings
            </h2>

            <div class={`grid grid-cols-1 gap-4 ${visibility() === 'private' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={status()}
                  onChange={(e) => {
                    setStatus(e.currentTarget.value as Project['status']);
                    markChanged();
                  }}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
                <select
                  value={visibility()}
                  onChange={(e) => {
                    setVisibility(e.currentTarget.value as Project['visibility']);
                    markChanged();
                  }}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors"
                >
                  <option value="private">Private</option>
                  <option value="group">Group</option>
                  <option value="public">Public</option>
                </select>
              </div>

              {/* Only show Run Mode for non-private projects (marketplace relevant) */}
              <Show when={visibility() !== 'private'}>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Run Mode</label>
                  <select
                    value={runMode()}
                    onChange={(e) => {
                      setRunMode(e.currentTarget.value as Project['run_mode']);
                      markChanged();
                    }}
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="both">Buy & Run</option>
                    <option value="buy">Buy Only</option>
                    <option value="run">Run Only (Service)</option>
                  </select>
                  <p class="text-xs text-gray-500 mt-1">
                    {runMode() === 'buy' && 'Users purchase full access'}
                    {runMode() === 'run' && 'Users pay per execution'}
                    {runMode() === 'both' && 'Users can buy or run as service'}
                  </p>
                </div>
              </Show>
            </div>
          </section>

          {/* Action Buttons */}
          <div class="flex items-center justify-between pt-4">
            <A
              href="/projects"
              class="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium transition-colors"
            >
              Cancel
            </A>

            <div class="flex items-center gap-3">
              <Show when={isEditMode()}>
                <button
                  type="button"
                  onClick={() => navigate(`/projects/${params.id}`)}
                  class="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium transition-colors"
                >
                  View Project
                </button>
              </Show>

              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving() || !name().trim() || !goal().trim()}
                class="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving() ? (
                  <span class="flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span>{isEditMode() ? 'Save Changes' : 'Create Project'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default ProjectBuilder;
